import { useSyncExternalStore } from 'react';
import { useChatStore } from '@/stores/chat.store';

// Subscribe to navigator.onLine via useSyncExternalStore for tear-free reads
function subscribeOnline(callback: () => void) {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

function getOnlineSnapshot() {
  return navigator.onLine;
}

function getServerSnapshot() {
  return true; // SSR assumes online
}

/** Reactive hook that tracks browser online/offline state */
export function useIsOnline() {
  return useSyncExternalStore(subscribeOnline, getOnlineSnapshot, getServerSnapshot);
}

/**
 * Inline banner for WebSocket disconnection (shown inside area-chat / thread-view).
 * The browser-offline banner is rendered globally in AppShell via useIsOnline,
 * so this component only shows the WS "Reconnecting..." state when the browser
 * is online but the WebSocket connection dropped.
 */
export function ConnectionStatus() {
  const isConnected = useChatStore((s) => s.isConnected);
  const isOnline = useIsOnline();

  // Don't show WS reconnecting when browser is offline — the global offline
  // banner in AppShell already communicates the problem.
  if (!isOnline || isConnected) return null;

  return (
    <div role="alert" className="flex items-center justify-center gap-2 bg-yellow-50 px-4 py-2 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Reconnecting...
    </div>
  );
}
